const ALLOWED_ORIGINS = ['*.absorblms.com', '*.absorbai.com', '*.togetherplatform.com', '*.webflow.io', 'localhost', '127.0.0.1'];

const MARKETO_CONFIG = {
    munchkinId: '840-JWB-417',
    defaultFormId: 0,
    baseUrl: 'https://discover.absorbai.com',
    scriptUrl: 'https://discover.absorbai.com/js/forms2/js/forms2.min.js',
};

const MESSAGE_TYPES = {
    HANDSHAKE_START: 'HANDSHAKE_START',
    HANDSHAKE_COMPLETE: 'HANDSHAKE_COMPLETE',
    FORM_DATA_UPDATE: 'FORM_DATA_UPDATE',
    FORM_SUBMIT: 'FORM_SUBMIT',
    FORM_SUBMIT_IN_PROGRESS: 'FORM_SUBMIT_IN_PROGRESS',
    FORM_SUBMIT_SUCCESS: 'FORM_SUBMIT_SUCCESS',
    FORM_SUBMIT_ERROR: 'FORM_SUBMIT_ERROR',
    DEBUG_MESSAGE: 'DEBUG_MESSAGE',
};

const state = {
    submissionEnabled: false,
    form: undefined,
    port: undefined,
    submissionTimeout: null,
    debugMode: false
};

// Pure functions
const isOriginAllowed = (origin) => {
    if (!origin) return false;

    try {
        const { hostname, protocol } = new URL(origin);
        
        if (protocol !== 'https:') return false;

        return ALLOWED_ORIGINS.some((domain) => {
            if (domain.startsWith('*.')) {
                const baseDomain = domain.substring(2);
                return hostname === baseDomain || hostname.endsWith(`.${baseDomain}`);
            }
            return hostname === domain;
        });
    } catch {
        return false;
    }
};

const extractMarketoValidationState = (form) => {
    const formElem = form && form.getFormElem ? form.getFormElem() : [];
    const [formEl] = formElem || [];
    if (!formEl) return [];

    return [...formEl.querySelectorAll('.mktoFieldWrap')]
        .map((fieldWrap) => {
            const input = fieldWrap.querySelector('input, select, textarea');
            return input && input.name ? {
                field: input.name,
                valid: !fieldWrap.querySelector('.mktoInvalid, .mktoError'),
                message: (function () {
                    const errorMsg = fieldWrap.querySelector('.mktoErrorMsg');
                    return errorMsg && errorMsg.textContent ? errorMsg.textContent.trim() : '';
                }()),
            } : null;
        })
        .filter(Boolean);
};

const applyFormData = (form, mappedData) => form.addHiddenFields(mappedData);

// Side effect functions
const sendMessage = (type, payload = {}) => {
    if (state.port) {
        state.port.postMessage({ type, ...payload });
    }
};

const sendDebugMessage = (message, data = {}) => {
    // Only log to console if debug mode is enabled
    if (state.debugMode) console.log(`[Marketo Iframe] ${message}`, data);
    
    sendMessage(MESSAGE_TYPES.DEBUG_MESSAGE, { message, data });
};

// Message handlers
const handleFormDataUpdate = (messageData) => {
    if (state.form) {
        applyFormData(state.form, messageData);
    }
};

const handleFormSubmit = (messageData) => {
    if (!state.submissionEnabled) {
        sendMessage(MESSAGE_TYPES.FORM_SUBMIT_ERROR, {
            error: 'Please refresh the page and try again.',
            fieldStatuses: [],
        });

        sendDebugMessage('Form initialization error');

        return;
    }

    if (!state.form) {
        sendMessage(MESSAGE_TYPES.FORM_SUBMIT_ERROR, {
            error: 'Please refresh the page and try again.',
            fieldStatuses: [],
        });

        sendDebugMessage('Form initialization error');
        
        return;
    }

    applyFormData(state.form, messageData);

    const isValid = state.form.validate();
    const fieldStatuses = extractMarketoValidationState(state.form);

    if (!isValid) {
        sendMessage(MESSAGE_TYPES.FORM_SUBMIT_ERROR, {
            error: 'Please complete all of the required fields and try again',
            fieldStatuses,
        });

        sendDebugMessage('Validations failed', {
            fieldStatuses,
            formId: state.form.getId ? state.form.getId() : 'Unknown',
        });
    } else {
        sendDebugMessage('Submitting Marketo form', { formId: state.form.getId ? state.form.getId() : 'Unknown' });
        
        // Set a timeout to send error message if no response
        state.submissionTimeout = setTimeout(() => {
            sendDebugMessage('We\'re having trouble sending this form. Please try again.', { 
                level: 'warning',
                timeoutMs: 10000
            });
            // Send proper error message for timeout
            sendMessage(MESSAGE_TYPES.FORM_SUBMIT_ERROR, {
                error: 'We\'re having trouble sending this form. Please try again.',
                fieldStatuses: [],
            });
            state.submissionTimeout = null;
        }, 10000);
        
        state.form.submit();
    }
};

const handleParentMessage = ({ data = {} }) => {
    const { type, data: messageData } = data;

    const handlers = {
        [MESSAGE_TYPES.FORM_DATA_UPDATE]: () => handleFormDataUpdate(messageData),
        [MESSAGE_TYPES.FORM_SUBMIT]: () => handleFormSubmit(messageData),
    };

    if (handlers[type]) {
        handlers[type]();
    }
};

const syncUrlWithParent = () => {
    try {
        if (window.parent && window.parent !== window && window.parent.location.href) {
            const parentUrl = window.parent.location.href;
            const currentUrl = window.location.href;
            
            if (parentUrl !== currentUrl) {
                sendDebugMessage('Syncing Marketo form iframe URL with parent', { parentUrl });
                window.history.replaceState(null, '', parentUrl);
            }
        }
    } catch (error) {
        sendDebugMessage('Cannot sync the Marketo form URL due to cross-origin restriction', { error: error.message });
    }
};

// Async initialization
const loadMarketoLibrary = async (scriptUrl) => {
    if (window.MktoForms2) {
        sendDebugMessage('Marketo library already loaded');

        return;
    }

    return await new Promise((resolve, reject) => {
        const script = Object.assign(document.createElement('script'), {
            src: scriptUrl,
            async: true,
            onload: () => (window.MktoForms2 ? resolve() : reject(new Error('Marketo Forms 2.0 library failed to load'))),
            onerror: () => reject(new Error('Unable to load Marketo integration')),
        });

        document.head.appendChild(script);
    });
};

const loadMarketoForm = async ({ baseUrl, formId, munchkinId }) => {
    const container = document.querySelector('form.marketo-form');

    if (!container) throw new Error('Marketo form container not found');
    if (!window.MktoForms2) throw new Error('MktoForms2 library not loaded');

    container.id = `mktoForm_${formId}`;
    sendDebugMessage('Loading Marketo form', { baseUrl, munchkinId, formId });

    return await new Promise((resolve) => {
        window.MktoForms2.loadForm(baseUrl, munchkinId, formId, resolve);
    });
};

const setupMarketoListeners = (form) => {
    form.onSubmit(() => {
        sendMessage(MESSAGE_TYPES.FORM_SUBMIT_IN_PROGRESS);
        return true;
    });

    form.onSuccess((data, followUpUrl = '') => {
        // Clear timeout since we got a real success
        if (state.submissionTimeout) {
            clearTimeout(state.submissionTimeout);

            state.submissionTimeout = 0;
        }
        
        sendMessage(MESSAGE_TYPES.FORM_SUBMIT_SUCCESS, {
            data,
            success: true,
            ...(followUpUrl && { followUpUrl })
        });

        return false; // Prevent default form submission
    });

    sendDebugMessage('Marketo listeners setup complete');
    return form;
};

const initializeMarketo = async (formId) => {
    const config = {
        munchkinId: MARKETO_CONFIG.munchkinId,
        formId: parseInt(formId, 10) || MARKETO_CONFIG.defaultFormId,
        baseUrl: MARKETO_CONFIG.baseUrl,
        scriptUrl: MARKETO_CONFIG.scriptUrl,
    };

    syncUrlWithParent();

    try {
        await loadMarketoLibrary(config.scriptUrl);
        const form = await loadMarketoForm(config);
        setupMarketoListeners(form);
        state.form = form;
        state.submissionEnabled = true;
        sendDebugMessage('Marketo initialized successfully', { formId: config.formId });
    } catch (error) {
        sendMessage(MESSAGE_TYPES.FORM_SUBMIT_ERROR, {
            error: `Marketo initialization failed: ${error.message}`,
            fieldStatuses: [],
        });
        throw error;
    }
};

const handleHandshakeStart = async ({ data = {}, ports = [], origin }) => {
    const { type, formId, debugMode = false } = data; // Extract debugMode

    if (!isOriginAllowed(origin)) {
        sendDebugMessage(`Handshake rejected: origin ${origin} not allowed`);
        return;
    }

    if (type !== MESSAGE_TYPES.HANDSHAKE_START || ports.length === 0 || !formId) {
        sendDebugMessage('Invalid handshake', { type, portsLength: ports.length, formId });
        return;
    }

    // Set debug mode in state
    state.debugMode = debugMode;
    [state.port] = ports;

    sendDebugMessage('Handshake received', { formId, debugMode });
    window.removeEventListener('message', handleHandshakeStart);

    state.port.onmessage = handleParentMessage;
    state.port.start();

    try {
        await initializeMarketo(formId);
        sendMessage(MESSAGE_TYPES.HANDSHAKE_COMPLETE);
        sendDebugMessage('Iframe ready and initialized', { timestamp: Date.now() });
    } catch (error) {
        sendDebugMessage('Initialization failed', { error: error.message });
    }
};

window.addEventListener('message', handleHandshakeStart);

// Debug logging
sendDebugMessage('Iframe ready', { timestamp: Date.now() });